import create from "zustand";
import { produce } from "immer";
import { extractVisualAcuity } from "../utils/helpers/newReportHelper";

export interface EyeReportState {
  eyeReportData: {
    clinicalExamination: ClinicalExamination;
    visualAcuity: {
      RightEye: VisualAcuity;
      LeftEye: VisualAcuity;
    };
  };

  EyeMetaData: EyeMetaData;
  EyesOtherData: EyesOtherProps;
  EyeSuggestedInvestigationData: EyeSuggestedInvestigation;
  NeedAttention: EyeAttentionProps;

  setEyeReportData: (data: Record<string, any>) => void;
  resetEyeReport: () => void;
}

const INITIAL_DATA = {
  eyeReportData: {
    clinicalExamination: {
      rightEye: {
        Clinical_Right_Eye_Amblyopia: false,
        Clinical_Right_Eye_Conjunctivis: false,
        Clinical_Right_Eye_Squint: false,
        Clinical_Right_Eye_Blepharitis: false,
        Clinical_Right_Eye_Trauma: false,
        Clinical_Right_Eye_Ptosis: false,
      },

      leftEye: {
        Clinical_Left_Eye_Amblyopia: false,
        Clinical_Left_Eye_Conjunctivis: false,
        Clinical_Left_Eye_Squint: false,
        Clinical_Left_Eye_Blepharitis: false,
        Clinical_Left_Eye_Trauma: false,
        Clinical_Left_Eye_Ptosis: false,
      },
    },

    visualAcuity: {
      RightEye: {
        unaided: null,
        aided: null,
      },

      LeftEye: {
        unaided: null,
        aided: null,
      },
    },
  },

  EyeMetaData: {
    Eye_Doctor_No: "",
    Eye_Doctor_Designation: "",
    Eye_Remarks: "",
    Eye_Doctor_Path: "",
    Eye_Doctor_Hospital: "",
  },

  EyesOtherData: {
    Clinical_Right_Eye_Others: "",
    Clinical_Left_Eye_Others: "",
    Eye_Other_Tests: "",
  },

  EyeSuggestedInvestigationData: {
    Eye_Doctor_Consultation: false,
    Refraction: false,
    HSG_Eye_Doctor_Consultation: false,
    HSG_Refraction: false,
    HSG_Spectacles: false,
    Spectacles: false,
  },

  NeedAttention: {
    rightEye: false,
    leftEye: false,
    eyeSuggestedInvestigation: false,
    visualAcuity: false,
  },
};

export const useEyeStore = create<EyeReportState>((set) => ({
  ...INITIAL_DATA,

  setEyeReportData: (responseData) =>
    set(
      produce((state: EyeReportState) => {
        if (!responseData) return;

        state.NeedAttention = {
          rightEye: false,
          leftEye: false,
          eyeSuggestedInvestigation: false,
          visualAcuity: false,
        };

        const clinical = state.eyeReportData.clinicalExamination;

        Object.keys(clinical.rightEye).forEach((key) => {
          const value = responseData[key] === "Y";

          clinical.rightEye[key] = value;

          if (value) {
            state.NeedAttention.rightEye = true;
          }
        });

        Object.keys(clinical.leftEye).forEach((key) => {
          const value = responseData[key] === "Y";

          clinical.leftEye[key] = value;

          if (value) {
            state.NeedAttention.leftEye = true;
          }
        });

        Object.keys(state.EyeMetaData).forEach((key) => {
          state.EyeMetaData[key] = responseData[key] || "";
        });

        Object.keys(state.EyesOtherData).forEach((key) => {
          state.EyesOtherData[key] = responseData[key] || "";
        });

        Object.keys(state.EyeSuggestedInvestigationData).forEach((key) => {
          const value = responseData[key] === "Y";

          state.EyeSuggestedInvestigationData[key] = value;

          if (value) {
            state.NeedAttention.eyeSuggestedInvestigation = true;
          }
        });

        state.eyeReportData.visualAcuity = {
          RightEye: {
            unaided: extractVisualAcuity(responseData, "Right_Eye", ""),

            aided: extractVisualAcuity(responseData, "Right_Eye", "Spectacles"),
          },

          LeftEye: {
            unaided: extractVisualAcuity(responseData, "Left_Eye", ""),

            aided: extractVisualAcuity(responseData, "Left_Eye", "Spectacles"),
          },
        };
      })
    ),

  resetEyeReport: () =>
    set({
      ...INITIAL_DATA,
    }),
}));
